/// <reference types="vite/client" />
import { reactRouterV6BrowserTracingIntegration } from "@sentry/react"
import * as Sentry from "@sentry/react-router"
import i18next from "i18next"
import { useEffect } from "react"
import { hydrateRoot } from "react-dom/client"
import { initReactI18next } from "react-i18next"
import {
  createRoutesFromChildren,
  HydratedRouter,
  matchRoutes,
  useLocation,
  useNavigationType
} from "react-router-dom"

const enableSentry =
  import.meta.env.PROD && import.meta.env.ENABLE_SENTRY === "true"
const dsn = import.meta.env.SENTRY_DSN
const release = import.meta.env.SENTRY_RELEASE

if (enableSentry && dsn && release) {
  Sentry.init({
    dsn,
    release,
    sendDefaultPii: true,
    integrations: [
      reactRouterV6BrowserTracingIntegration({
        useEffect,
        useLocation,
        useNavigationType,
        createRoutesFromChildren,
        matchRoutes
      })
    ],
    enableLogs: true,
    tracesSampleRate: 1.0
  })
}

// Add CloudFront to Vercel fallback for static assets
const setupAssetFallback = () => {
  const cloudfrontUrl = import.meta.env.CLOUDFRONT_URL

  if (!cloudfrontUrl) {
    // No CloudFront URL configured, nothing to fallback from
    return
  }

  // Listen for asset loading errors
  window.addEventListener("error", (event) => {
    const target = event.target as HTMLElement

    // Check if it's a script or link tag that failed to load
    if (target && (target.tagName === "SCRIPT" || target.tagName === "LINK")) {
      const scriptTarget = target as HTMLScriptElement
      const linkTarget = target as HTMLLinkElement
      const assetUrl = scriptTarget.src || linkTarget.href

      // Check if the failed asset is from CloudFront
      if (assetUrl && assetUrl.includes(cloudfrontUrl)) {
        // Prevent the error from being logged to console (optional)
        event.preventDefault()

        // Construct fallback URL by replacing CloudFront domain with current origin (Vercel)
        const fallbackUrl = assetUrl.replace(
          cloudfrontUrl,
          window.location.origin
        )

        console.warn(
          `[Asset Fallback] Failed to load from CloudFront, retrying from Vercel:`,
          {
            original: assetUrl,
            fallback: fallbackUrl
          }
        )

        // Create a new element to load from Vercel
        if (target.tagName === "SCRIPT") {
          const fallbackScript = document.createElement("script")
          fallbackScript.src = fallbackUrl
          fallbackScript.async = scriptTarget.async
          fallbackScript.defer = scriptTarget.defer
          document.head.appendChild(fallbackScript)
        } else if (target.tagName === "LINK") {
          const fallbackLink = document.createElement("link")
          fallbackLink.rel = "stylesheet"
          fallbackLink.href = fallbackUrl
          document.head.appendChild(fallbackLink)
        }
      }
    }
  })
}

const initialI18nStore = window.__I18N_STORE__
const initialLanguage = window.__I18N_LANGUAGE__

// Setup asset fallback before hydration
setupAssetFallback()

// eslint-disable-next-line import/no-named-as-default-member
i18next
  .use(initReactI18next)
  .init({
    resources: {
      [initialLanguage]: {
        common: initialI18nStore
      }
    },
    lng: initialLanguage,
    ns: ["common"],
    defaultNS: "common",
    fallbackLng: "en",
    interpolation: { escapeValue: false }
  })
  .then(() => {
    hydrateRoot(document, <HydratedRouter />)
  })
